'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Search, X, MessageSquare, Users, User } from 'lucide-react';

type FilterType = 'all' | 'messages' | 'contacts' | 'groups';

interface SearchResult {
  id: string;
  type: 'message' | 'contact' | 'group';
  name: string;
  initials: string;
  avatarGradient: string;
  subtitle: string;
  timestamp?: string;
  isOnline?: boolean;
  highlight?: string;
}

const ALL_RESULTS: SearchResult[] = [
  // Contacts
  {
    id: 'c1',
    type: 'contact',
    name: 'Ate Boni',
    initials: 'AB',
    avatarGradient: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
    subtitle: 'last seen Mar 08 at 1:43 AM',
    isOnline: true,
  },
  {
    id: 'c2',
    type: 'contact',
    name: 'Marco Reyes',
    initials: 'MR',
    avatarGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    subtitle: '+63 912 345 6789',
    isOnline: true,
  },
  {
    id: 'c3',
    type: 'contact',
    name: 'Ana Santos',
    initials: 'AS',
    avatarGradient: 'linear-gradient(135deg, #06B6D4 0%, #0284C7 100%)',
    subtitle: '+63 917 654 3210',
    isOnline: true,
  },
  {
    id: 'c4',
    type: 'contact',
    name: 'Kuya Dan',
    initials: 'KD',
    avatarGradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    subtitle: 'last seen yesterday',
  },
  {
    id: 'c5',
    type: 'contact',
    name: 'Paolo Cruz',
    initials: 'PC',
    avatarGradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
    subtitle: 'last seen 3 days ago',
  },
  {
    id: 'c6',
    type: 'contact',
    name: 'Lola Nena',
    initials: 'LN',
    avatarGradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    subtitle: 'last seen Saturday',
  },
  // Groups
  {
    id: 'g1',
    type: 'group',
    name: 'Design Team',
    initials: 'DT',
    avatarGradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    subtitle: '8 members',
  },
  {
    id: 'g2',
    type: 'group',
    name: 'Jess & Friends',
    initials: 'JF',
    avatarGradient: 'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)',
    subtitle: '14 members',
  },
  {
    id: 'g3',
    type: 'group',
    name: 'Work Updates',
    initials: 'WU',
    avatarGradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
    subtitle: '22 members',
  },
  {
    id: 'g4',
    type: 'group',
    name: 'Barkada GC',
    initials: 'BG',
    avatarGradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    subtitle: '11 members',
  },
  // Messages
  {
    id: 'm1',
    type: 'message',
    name: 'Ate Boni',
    initials: 'AB',
    avatarGradient: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
    subtitle: 'nakita ko na, salamat! 🎉',
    timestamp: '11:38 AM',
    highlight: 'salamat',
  },
  {
    id: 'm2',
    type: 'message',
    name: 'Design Team',
    initials: 'DT',
    avatarGradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    subtitle: 'Check the latest mockups 🔥',
    timestamp: '10:15 AM',
    highlight: 'mockups',
  },
  {
    id: 'm3',
    type: 'message',
    name: 'Marco Reyes',
    initials: 'MR',
    avatarGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    subtitle: 'Sige, bukas na lang tayo mag-meet',
    timestamp: 'Yesterday',
    highlight: 'bukas',
  },
  {
    id: 'm4',
    type: 'message',
    name: 'Kuya Dan',
    initials: 'KD',
    avatarGradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    subtitle: 'Padala mo na yung file mamaya',
    timestamp: 'Mon',
    highlight: 'file',
  },
  {
    id: 'm5',
    type: 'message',
    name: 'Lola Nena',
    initials: 'LN',
    avatarGradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    subtitle: 'Ingat ka palagi ha anak 🙏',
    timestamp: 'Sat',
    highlight: 'Ingat',
  },
];

interface SearchScreenProps {
  onBack: () => void;
}

export default function SearchScreen({ onBack }: SearchScreenProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = ALL_RESULTS.filter(r => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'messages' && r.type === 'message') ||
      (filter === 'contacts' && r.type === 'contact') ||
      (filter === 'groups' && r.type === 'group');

    const q = query.toLowerCase().trim();
    const matchesQuery =
      !q ||
      r.name.toLowerCase().includes(q) ||
      r.subtitle.toLowerCase().includes(q);

    return matchesFilter && matchesQuery;
  });

  // Group results by type for "all" view
  const grouped: { label: string; items: SearchResult[] }[] = [];
  if (filter === 'all') {
    const messages = filtered.filter(r => r.type === 'message');
    const contacts = filtered.filter(r => r.type === 'contact');
    const groups = filtered.filter(r => r.type === 'group');
    if (messages.length) grouped.push({ label: 'Messages', items: messages });
    if (contacts.length) grouped.push({ label: 'Contacts', items: contacts });
    if (groups.length) grouped.push({ label: 'Groups', items: groups });
  } else {
    if (filtered.length) grouped.push({ label: '', items: filtered });
  }

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#101C29',
      }}
    >
      {/* Header with search input */}
      <header
        className="flex items-center gap-2 px-3 flex-shrink-0"
        style={{
          height: '64px',
          background: 'rgba(16,28,41,0.97)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <button
          onClick={onBack}
          className="flex items-center justify-center w-[40px] h-[40px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150 flex-shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft size={22} className="text-white" />
        </button>

        {/* Search input */}
        <div
          className="flex-1 flex items-center gap-2.5 px-3.5 rounded-2xl"
          style={{ background: 'rgba(255,255,255,0.08)', height: '40px' }}
        >
          <Search size={16} className="flex-shrink-0" style={{ color: 'rgba(255,255,255,0.4)' }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search messages, contacts, groups…"
            className="flex-1 bg-transparent outline-none text-white text-[14px] placeholder:text-white/35"
            aria-label="Search"
          />
          {query.length > 0 && (
            <button
              onClick={() => setQuery('')}
              className="flex-shrink-0 flex items-center justify-center w-[20px] h-[20px] rounded-full transition-colors"
              style={{ background: 'rgba(255,255,255,0.18)' }}
              aria-label="Clear search"
            >
              <X size={12} className="text-white" />
            </button>
          )}
        </div>
      </header>

      {/* Filter bar */}
      <div
        className="flex items-center gap-2 px-4 py-3 flex-shrink-0 overflow-x-auto"
        style={{
          background: '#101C29',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          scrollbarWidth: 'none',
        }}
      >
        {(
          [
            { key: 'all', label: 'All', icon: null },
            { key: 'messages', label: 'Messages', icon: <MessageSquare size={13} /> },
            { key: 'contacts', label: 'Contacts', icon: <User size={13} /> },
            { key: 'groups', label: 'Groups', icon: <Users size={13} /> },
          ] as { key: FilterType; label: string; icon: React.ReactNode }[]
        ).map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium flex-shrink-0 transition-all duration-150"
            style={
              filter === tab.key
                ? {
                    background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
                    color: '#fff',
                  }
                : {
                    background: 'rgba(255,255,255,0.07)',
                    color: 'rgba(255,255,255,0.55)',
                  }
            }
            aria-pressed={filter === tab.key}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        {grouped.length === 0 ? (
          <EmptyState query={query} />
        ) : (
          grouped.map(group => (
            <div key={group.label}>
              {group.label ? (
                <div
                  className="px-4 pt-5 pb-2 text-[11px] font-semibold uppercase tracking-widest"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                >
                  {group.label}
                </div>
              ) : null}
              {group.items.map((result, idx) => (
                <ResultRow
                  key={result.id}
                  result={result}
                  isLast={idx === group.items.length - 1}
                  query={query}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ResultRow({
  result,
  isLast,
  query,
}: {
  result: SearchResult;
  isLast: boolean;
  query: string;
}) {
  return (
    <button
      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100 active:scale-[0.99]"
      style={{
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
        background: 'transparent',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div
          className="w-[48px] h-[48px] rounded-full flex items-center justify-center text-white font-bold text-[15px]"
          style={{ background: result.avatarGradient }}
          aria-label={`${result.name} avatar`}
        >
          {result.initials}
        </div>
        {result.isOnline && (
          <span
            className="absolute bottom-[1px] right-[1px] w-[12px] h-[12px] bg-green-400 rounded-full border-2"
            style={{ borderColor: '#101C29' }}
            aria-label="Online"
          />
        )}
        {result.type === 'group' && (
          <span
            className="absolute bottom-[-2px] right-[-2px] w-[18px] h-[18px] rounded-full flex items-center justify-center"
            style={{ background: '#1E3248', border: '1.5px solid rgba(255,255,255,0.1)' }}
          >
            <Users size={10} style={{ color: 'rgba(255,255,255,0.6)' }} />
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-[2px]">
          <HighlightedText
            text={result.name}
            query={query}
            className="text-[15px] font-semibold truncate"
            style={{ color: 'rgba(255,255,255,0.92)' }}
          />
          {result.timestamp && (
            <span className="text-[12px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.38)' }}>
              {result.timestamp}
            </span>
          )}
        </div>
        <HighlightedText
          text={result.subtitle}
          query={query}
          className="text-[13px] truncate leading-snug"
          style={{ color: 'rgba(255,255,255,0.42)' }}
        />
      </div>
    </button>
  );
}

function HighlightedText({
  text,
  query,
  className,
  style,
}: {
  text: string;
  query: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (!query.trim()) {
    return (
      <span className={className} style={style}>
        {text}
      </span>
    );
  }

  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));

  return (
    <span className={className} style={style}>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={i}
            style={{
              background: 'rgba(40,127,216,0.35)',
              color: '#7EC8FF',
              borderRadius: '2px',
              padding: '0 1px',
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

function EmptyState({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 px-8 text-center" style={{ paddingTop: '80px' }}>
      <div
        className="w-[64px] h-[64px] rounded-full flex items-center justify-center"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      >
        <Search size={28} style={{ color: 'rgba(255,255,255,0.25)' }} />
      </div>
      {query ? (
        <>
          <p className="text-[16px] font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
            No results for "{query}"
          </p>
          <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Try a different keyword or filter
          </p>
        </>
      ) : (
        <>
          <p className="text-[16px] font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Search anything
          </p>
          <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Find messages, contacts, or groups
          </p>
        </>
      )}
    </div>
  );
}
